import { useState, useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import { OLITASK_SERVICE_BASE_URL } from "constants";
import apiClient from "utils/apiClient";

const API_BASE_URL = `${OLITASK_SERVICE_BASE_URL}/api/v1/credits`;

export default function useCredits() {
  const authToken = sessionStorage.getItem("authToken");
  const [credits, setCredits] = useState([]);
  const [selectedCredit, setSelectedCredit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);


  // handle axios errors
  const handleAxiosErrors = (err, action = "operation") => {
    let message = "An unexpected error occurred.";
    if (err.response) {
      message =
        err.response.data?.message ||
        `Failed to ${action}: ${err.response.statusText}`;
    } else if (err.request) {
      message =
        "No response from the server. Please check your internet connection.";
    } else {
      message = err.message || "Something went wrong.";
    }
    setError(message);
    toast.error(message);
    console.error(`${action} error:`, err);
  };


  const fetchCredits = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const firstResponse = await apiClient.get(
        `${API_BASE_URL}?page=0&size=${50}`
      );
      const { content: firstContent = [], totalPages = 1 } = firstResponse.data;
      let allCredits = [...firstContent];

      if (totalPages > 1) {
        const promises = [];
        for (let p = 1; p < totalPages; p++) {
          promises.push(
            apiClient.get(`${API_BASE_URL}?page=${p}&size=${50}`)
          );
        }

        const responses = await Promise.all(promises);
        responses.forEach((res) => {
          allCredits = allCredits.concat(res.data.content || []);
        });
      }
      setCredits(allCredits);
    } catch (err) {
      handleAxiosErrors(err, "Fetch credits");
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  // Fetch one API key by ID
  const fetchCreditById = useCallback(
    async (id) => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiClient.get(`${API_BASE_URL}/${id}`);
        setSelectedCredit(res.data);
        return res.data;
      } catch (err) {
        handleAxiosErrors(err, "fetch credit details");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authToken]
  );

  // Delete credit
  const deleteCredit = useCallback(
    async (id) => {
      setLoading(true);
      setError(null);
      try {
        await apiClient.delete(`${API_BASE_URL}/${id}`);

        // Remove deleted credit from local state
        setCredits((prev) => prev.filter((key) => key.id !== id));
        toast.success("Credit deleted successfully!", {
          theme: "colored",
        });
      } catch (err) {
        handleAxiosErrors(err, "Delete credit");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authToken]
  );

  // fetch automatically on mount
  useEffect(() => {
    if (authToken) fetchCredits();
  }, [authToken, fetchCredits]);

  return {
    credits,
    selectedCredit,
    loading,
    error,
    fetchCredits,
    fetchCreditById,
    deleteCredit,
    setSelectedCredit,
  };
}
